<script setup lang="ts">
import { urlChangeTheme } from '@/api/endpoint'
import { isAllowedHost, URL_BACK } from '@/config/constants'
import { applyCardTheme, VALID_THEMES_CARD } from '@/event/theme/card'
import { applyTheme, VALID_THEMES } from '@/event/theme/theme'
import stores from '@/storage/stores'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const messageResponse = ref<{ message: string; class: string }>({
    message: '',
    class: ''
})
const selectedTheme = ref('')
const selectedCard = ref('')
const showThemeModal = ref(false)
const showThemeDropdown = ref(false)
const showCardDropdown = ref(false)

const handleThemeChange = async (event: string) => {
    try {
        const theme = event
        selectedTheme.value = theme
        applyTheme(theme)

        const url = `https://${URL_BACK}/api/${urlChangeTheme}`

        if (storeId.value) {
            const res = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ theme, store_id: storeId.value }),
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            const response = await res.json()
            response.message = response.message + ` ${theme}`
            messageResponse.value = response
        }
    } catch (e) {
        // console.log(e)
    }
}

function handleCardChange(event: string) {
    selectedCard.value = event
    applyCardTheme(event)
}

const storeId = computed(() => stores.getStoresData().store_id)

const toggleModalChangeTheme = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.altKey && e.key === 't') {
        showThemeModal.value = !showThemeModal.value
        // checkModalOpen()
    }
}

function toggleShowModal() {
    showThemeModal.value = !showThemeModal.value
    // checkModalOpen()
}

const showDevSelector = computed(() => {
    return isAllowedHost() && showThemeModal.value === true
})

async function scrollToSelected(containerClass: string) {
    await nextTick()
    const container = document.querySelector(`.${containerClass}`) as HTMLElement
    const selectedOption = container.querySelector('.selected') as HTMLElement
    if (container && selectedOption) {
        const containerHeight = container.clientHeight
        const optionTop = selectedOption.offsetTop
        const optionHeight = selectedOption.clientHeight

        container.scrollTop = optionTop - containerHeight / 2 + optionHeight / 2
    }
}

async function toggleSelectThemeDropdown() {
    showThemeDropdown.value = !showThemeDropdown.value
    if (showThemeDropdown.value) {
        await scrollToSelected('select-options')
    }
}

async function toggleSelectCardDropdown() {
    showCardDropdown.value = !showCardDropdown.value
    if (showCardDropdown.value) {
        await scrollToSelected('select-options')
    }
}

async function checkModalOpen() {
    const htmlCloseScroll = document.documentElement
    if (showThemeModal.value) {
        htmlCloseScroll.classList.add('modal-open')
    } else {
        htmlCloseScroll.classList.remove('modal-open')
    }
}

onMounted(async () => {
    const themeSaved = localStorage.getItem('theme')
    const cardSaved = localStorage.getItem('card')
    selectedTheme.value = themeSaved ? themeSaved : ''
    selectedCard.value = cardSaved ? cardSaved : ''
    // checkModalOpen()

    window.addEventListener('keydown', toggleModalChangeTheme)

    document.addEventListener('click', (event) => {
        const selectThemeInput = document.getElementById(
            'themeSelect'
        ) as HTMLSelectElement
        const selectCardInput = document.getElementById('cardSelect') as HTMLSelectElement
        const target = event.target as HTMLElement

        if (!selectCardInput?.contains(target)) {
            showCardDropdown.value = false
        }
        if (!selectThemeInput?.contains(target)) {
            showThemeDropdown.value = false
        }
    })
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', toggleModalChangeTheme)
})
</script>

<template>
    <section v-if="showDevSelector" class="section-change-theme">
        <!-- <div class="overlay-change-theme"></div> -->
        <div class="theme-selector">
            <div class="div-close">
                <span></span>
                <button class="p-close" @click="toggleShowModal">X</button>
            </div>

            <div class="custom-select">
                <h4>Changer le thème :</h4>
                <div
                    id="themeSelect"
                    class="select-header"
                    @click="toggleSelectThemeDropdown()"
                >
                    {{ selectedTheme || 'Sélectionner un thème' }}
                    <span class="arrow" :class="{ 'rotate-180': showThemeDropdown }">
                        ▼
                    </span>
                </div>
                <div v-if="showThemeDropdown" class="select-options">
                    <div
                        v-for="theme in VALID_THEMES"
                        :key="theme"
                        class="select-option"
                        :class="{ selected: selectedTheme === theme }"
                        @click="handleThemeChange(theme)"
                    >
                        {{ theme }}
                    </div>
                </div>
                <p
                    class="font-semibold text-center py-2 mt-2 rounded-md"
                    :class="{
                        'bg-urban-red text-slate-medium-dark':
                            messageResponse.class === 'error',
                        'bg-green-light-happy text-urban-dark':
                            messageResponse.class === 'success'
                    }"
                >
                    {{ messageResponse.message }}
                </p>
            </div>

            <div class="custom-select">
                <h4>Changer le thème de la carte :</h4>
                <div
                    class="select-header"
                    id="cardSelect"
                    @click="toggleSelectCardDropdown()"
                >
                    {{ selectedCard || 'Sélectionner une carte' }}
                    <span class="arrow" :class="{ 'rotate-180': showCardDropdown }"
                        >▼</span
                    >
                </div>
                <div v-if="showCardDropdown" class="select-options">
                    <div
                        v-for="card in Object.values(VALID_THEMES_CARD)"
                        :key="card"
                        class="select-option"
                        :class="{ selected: selectedCard === card }"
                        @click="handleCardChange(card)"
                    >
                        {{ card }}
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.section-change-theme {
    @apply fixed z-50 w-full top-0 h-screen flex flex-col items-center justify-center pointer-events-none;
}

.overlay-change-theme {
    @apply absolute top-0 w-full h-full bg-black opacity-50  z-20;
}

.theme-selector {
    /* refactored */
    @apply flex flex-col gap-2 justify-center relative p-3 rounded-md z-50 w-56;
    @apply bg-t-card text-t-text w-72 pointer-events-auto border border-t-border;
}

.div-close {
    @apply flex justify-between;
}
.p-close {
    @apply cursor-pointer p-1 scale-105;
}
.p-close:active,
.p-close:focus {
    @apply text-urban-red font-semibold;
}

.custom-select {
    @apply relative w-full;
}

.select-header {
    /* refactored */
    @apply p-2 rounded border cursor-pointer flex justify-between items-center;
    @apply bg-t-bg text-t-text border-t-border overflow-y-auto w-full;
}

.arrow {
    @apply text-lg scale-105 transition-transform duration-200;
}

.select-options {
    /* refactored */
    @apply absolute w-full rounded mt-1 max-h-60 overflow-y-auto z-10 border;
    @apply bg-t-card border-t-border;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.select-option {
    /* refactored */
    @apply p-2 cursor-pointer w-full border-b;
    @apply text-t-text bg-t-card border-t-border;
}
.select-option:nth-of-type(odd) {
    /* refactored */
    @apply bg-t-bg;
}
.select-option:last-of-type {
    @apply border-none;
}

.select-option.selected {
    /* refactored */
    @apply bg-t-accent text-t-on-accent font-bold;
}

@media (hover: hover) {
    .p-close:hover {
        @apply text-urban-red font-semibold;
    }

    .select-option:hover {
        /* refactored */
        @apply bg-t-accent/25 text-t-text;
    }
}
</style>
